import React, { useEffect, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Building2, MapPin, Clock, Crosshair } from "lucide-react";
import { localizeError } from "@/lib/errors";
import AreaPicker from "@/components/marketplace/AreaPicker";
import { MapPicker } from "@/components/marketplace/MpMap";
import CoordsInput from "@/components/marketplace/CoordsInput";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = {
  name_en: "", name_ar: "", address: "", phone: "", email: "",
  timezone: "Asia/Riyadh", shared_inventory: false, active: true,
  // Attendance settings (Completion Pass v2)
  lat: null, lng: null, geofence_radius_m: null, attendance_grace_minutes: 10,
  // Marketplace zone (Qatar municipalities/areas).
  area_id: null,
  working_hours: {},
};

const DAYS = [
  { k: "saturday",  ar: "السبت",     en: "Sat" },
  { k: "sunday",    ar: "الأحد",     en: "Sun" },
  { k: "monday",    ar: "الإثنين",   en: "Mon" },
  { k: "tuesday",   ar: "الثلاثاء",  en: "Tue" },
  { k: "wednesday", ar: "الأربعاء",  en: "Wed" },
  { k: "thursday",  ar: "الخميس",    en: "Thu" },
  { k: "friday",    ar: "الجمعة",    en: "Fri" },
];

export default function Branches() {
  const { lang, t } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [section, setSection] = useState("basic"); // basic | attendance

  const load = async () => { const r = await api.get("/branches"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = {
        ...form,
        lat: form.lat === "" || form.lat == null ? null : parseFloat(form.lat),
        lng: form.lng === "" || form.lng == null ? null : parseFloat(form.lng),
        geofence_radius_m: form.geofence_radius_m === "" || form.geofence_radius_m == null ? null : parseInt(form.geofence_radius_m),
        attendance_grace_minutes: parseInt(form.attendance_grace_minutes || 0),
      };
      if (editId) await api.put(`/branches/${editId}`, payload);
      else await api.post("/branches", payload);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      setOpen(false); setForm(empty); setEditId(null); setSection("basic"); load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف الفرع؟" : "Delete branch?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/branches/${id}`); load();
  };

  const edit = (b) => { setForm({ ...empty, ...b, working_hours: b.working_hours || {} }); setEditId(b.branch_id); setOpen(true); setSection("basic"); };

  const useMyLocation = () => {
    if (!navigator.geolocation) { toast.error(lang === "ar" ? "تحديد الموقع غير متوفر" : "Geolocation not available"); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f, lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }));
        toast.success(lang === "ar" ? "تم التقاط الموقع" : "Location captured");
      },
      (err) => toast.error((lang === "ar" ? "تعذر التقاط الموقع: " : "Could not capture location: ") + err.message),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const setDay = (k, patch) => setForm(f => ({
    ...f,
    working_hours: {
      ...(f.working_hours || {}),
      [k]: { open: "10:00", close: "23:00", closed: false, ...(f.working_hours?.[k] || {}), ...patch },
    },
  }));

  return (
    <div className="space-y-6" data-testid="branches-page">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-heading gold-text">{lang === "ar" ? "الفروع" : "Branches"}</h1>
        <button data-testid="add-branch-btn" onClick={() => { setForm(empty); setEditId(null); setOpen(true); setSection("basic"); }}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-[var(--gold)] text-white"><Plus size={16} /> {lang === "ar" ? "إضافة فرع" : "Add Branch"}</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((b) => (
          <div key={b.branch_id} className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-5 flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-md bg-[var(--gold)]/10 flex items-center justify-center text-[var(--gold)]"><Building2 size={20} /></div>
                <div>
                  <h3 className="font-semibold text-[var(--text)]">{lang === "ar" ? b.name_ar : b.name_en}</h3>
                  <p className="text-xs text-[var(--text-muted)]">{b.address}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => edit(b)} className="p-1.5 rounded hover:bg-[var(--gold)]/10 text-[var(--gold)]"><Pencil size={14} /></button>
                <button data-testid={`delete-branch-${b.branch_id}`} onClick={() => remove(b.branch_id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500"><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="text-xs text-[var(--text-muted)] flex flex-wrap gap-2 mt-2">
              {b.phone && <span>📞 {b.phone}</span>}
              {b.email && <span>✉️ {b.email}</span>}
              {b.lat != null && b.lng != null && (
                <span className="px-2 py-0.5 rounded bg-[var(--gold)]/10 text-[var(--gold)] flex items-center gap-1">
                  <MapPin size={10}/>{b.geofence_radius_m ? `${b.geofence_radius_m}م` : (lang === "ar" ? "موقع محدد" : "geolocated")}
                </span>
              )}
              {b.shared_inventory && <span className="px-2 py-0.5 rounded bg-[var(--gold)]/10 text-[var(--gold)]">{lang === "ar" ? "مخزون مشترك" : "Shared Inventory"}</span>}
              <span className={`px-2 py-0.5 rounded ${b.active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{b.active ? (lang === "ar" ? "نشط" : "Active") : (lang === "ar" ? "غير نشط" : "Inactive")}</span>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="md:col-span-3 text-center text-[var(--text-muted)] py-12">{lang === "ar" ? "لا توجد فروع. أضف فرعك الأول." : "No branches yet. Add your first branch."}</div>}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="bg-[var(--surface)] rounded-xl p-6 w-full max-w-2xl max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h3 className="text-xl font-heading mb-4">{editId ? (lang === "ar" ? "تعديل فرع" : "Edit Branch") : (lang === "ar" ? "فرع جديد" : "New Branch")}</h3>

            <div className="flex gap-1 mb-4 border-b border-[var(--border)] -mt-2">
              <button onClick={() => setSection("basic")} data-testid="sec-basic"
                className={`px-4 py-2 text-sm border-b-2 ${section === "basic" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-muted)]"}`}>
                {lang === "ar" ? "البيانات الأساسية" : "Basic"}
              </button>
              <button onClick={() => setSection("attendance")} data-testid="sec-attendance"
                className={`px-4 py-2 text-sm border-b-2 ${section === "attendance" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-muted)]"}`}>
                {lang === "ar" ? "إعدادات الحضور" : "Attendance"}
              </button>
              <button onClick={() => setSection("location")} data-testid="sec-location"
                className={`px-4 py-2 text-sm border-b-2 ${section === "location" ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-muted)]"}`}>
                {lang === "ar" ? "الموقع والماركت بليس" : "Location & Marketplace"}
              </button>
            </div>

            {section === "basic" && (
              <div className="space-y-3">
                <input data-testid="branch-name-en" placeholder="Name (EN)" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} />
                <input data-testid="branch-name-ar" placeholder="الاسم (AR)" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
                <input placeholder={lang === "ar" ? "العنوان" : "Address"} className="w-full px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder={lang === "ar" ? "الهاتف" : "Phone"} className="px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <input placeholder={lang === "ar" ? "البريد الإلكتروني" : "Email"} className="px-3 py-2 border rounded-md bg-[var(--bg)]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.shared_inventory} onChange={(e) => setForm({ ...form, shared_inventory: e.target.checked })} /> {lang === "ar" ? "مخزون مشترك مع المركز الرئيسي" : "Shared inventory with main"}</label>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> {lang === "ar" ? "نشط" : "Active"}</label>
              </div>
            )}

            {section === "attendance" && (
              <div className="space-y-5" data-testid="branch-attendance-section">
                <div className="border border-[var(--border)] rounded-lg p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2"><MapPin size={14} className="text-[var(--gold)]"/><h4 className="font-semibold text-sm">{lang === "ar" ? "النطاق الجغرافي للحضور" : "Attendance Geofence"}</h4></div>
                    <button onClick={useMyLocation} type="button" data-testid="use-my-location"
                      className="px-3 py-1 rounded text-xs border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)]/10">
                      {lang === "ar" ? "التقط موقعي الحالي" : "Use my current location"}
                    </button>
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mb-3">
                    {lang === "ar"
                      ? "حدد الموقع الجغرافي للفرع ونصف القطر المسموح به (بالأمتار). أي تسجيل حضور خارج هذه الدائرة سيُعلَّم تلقائياً كـ \"خارج النطاق\". اترك نصف القطر فارغاً لإيقاف هذا الفحص."
                      : "Set the branch coordinates and the allowed radius (in metres). Any check-in outside this circle is automatically flagged \"out of range\". Leave radius empty to disable the check."}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[10px] uppercase text-[var(--text-muted)]">Lat</label>
                      <input type="number" step="0.000001" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)] num-display"
                        value={form.lat ?? ""} onChange={e => setForm({ ...form, lat: e.target.value })} data-testid="branch-lat"/>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[var(--text-muted)]">Lng</label>
                      <input type="number" step="0.000001" className="w-full px-3 py-2 border rounded-md bg-[var(--bg)] num-display"
                        value={form.lng ?? ""} onChange={e => setForm({ ...form, lng: e.target.value })} data-testid="branch-lng"/>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[var(--text-muted)]">{lang === "ar" ? "نصف القطر المسموح (متر)" : "Allowed radius (m)"}</label>
                      <input type="number" min="0" step="10" placeholder={lang === "ar" ? "مثال: 150" : "e.g. 150"}
                        className="w-full px-3 py-2 border rounded-md bg-[var(--bg)] num-display"
                        value={form.geofence_radius_m ?? ""} onChange={e => setForm({ ...form, geofence_radius_m: e.target.value })}
                        data-testid="branch-radius"/>
                    </div>
                    <div>
                      <label className="text-[10px] uppercase text-[var(--text-muted)]">{lang === "ar" ? "سماح التأخير (دقيقة)" : "Grace minutes"}</label>
                      <input type="number" min="0" max="120" step="1"
                        className="w-full px-3 py-2 border rounded-md bg-[var(--bg)] num-display"
                        value={form.attendance_grace_minutes ?? 10} onChange={e => setForm({ ...form, attendance_grace_minutes: e.target.value })}
                        data-testid="branch-grace"/>
                    </div>
                  </div>
                  {form.lat != null && form.lng != null && form.lat !== "" && form.lng !== "" && (
                    <a href={`https://maps.google.com/?q=${form.lat},${form.lng}`} target="_blank" rel="noreferrer"
                      className="mt-3 text-xs gold-text underline inline-block">
                      {lang === "ar" ? "افتح في خرائط جوجل" : "Open in Google Maps"}
                    </a>
                  )}
                </div>

                <div className="border border-[var(--border)] rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Clock size={14} className="text-[var(--gold)]"/>
                    <h4 className="font-semibold text-sm">{lang === "ar" ? "ساعات العمل الأسبوعية" : "Weekly Working Hours"}</h4>
                  </div>
                  <p className="text-[11px] text-[var(--text-muted)] mb-3">
                    {lang === "ar"
                      ? "تُستخدم وقت الفتح لاحتساب التأخير في تسجيل الدخول. ضع أيام الإجازة \"مغلق\"."
                      : "Open time is used to compute check-in lateness. Mark off-days as Closed."}
                  </p>
                  <div className="space-y-1.5">
                    {DAYS.map(d => {
                      const cfg = form.working_hours?.[d.k] || { open: "10:00", close: "23:00", closed: false };
                      return (
                        <div key={d.k} className="flex items-center gap-2 text-sm">
                          <span className="w-20 text-[var(--text-muted)]">{lang === "ar" ? d.ar : d.en}</span>
                          <label className="flex items-center gap-1 text-xs">
                            <input type="checkbox" checked={!!cfg.closed} onChange={e => setDay(d.k, { closed: e.target.checked })} data-testid={`closed-${d.k}`}/>
                            {lang === "ar" ? "مغلق" : "Closed"}
                          </label>
                          {!cfg.closed && (
                            <>
                              <input type="time" value={cfg.open || "10:00"} onChange={e => setDay(d.k, { open: e.target.value })}
                                className="px-2 py-1 border rounded bg-[var(--bg)] text-xs num-display" data-testid={`open-${d.k}`}/>
                              <span className="text-[var(--text-muted)]">→</span>
                              <input type="time" value={cfg.close || "23:00"} onChange={e => setDay(d.k, { close: e.target.value })}
                                className="px-2 py-1 border rounded bg-[var(--bg)] text-xs num-display"/>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {section === "location" && (
              <div className="space-y-5" data-testid="branch-location-section">
                <div>
                  <label className="text-[10px] uppercase text-[var(--text-muted)] block mb-1.5 font-semibold tracking-wider">
                    {lang === "ar" ? "المنطقة (للماركت بليس)" : "Marketplace area"}
                  </label>
                  <AreaPicker
                    value={form.area_id}
                    onChange={(area_id) => setForm({ ...form, area_id })}
                    testId="branch-area-picker"
                  />
                  <p className="text-[11px] text-[var(--text-muted)] mt-1.5">
                    {lang === "ar"
                      ? "اختر منطقة قطر التي يقع فيها الفرع — يُستخدم في الماركت بليس لتصنيف الصالون حسب الحي والبحث الجغرافي."
                      : "Pick the Qatar zone where this branch is located — used by the marketplace to group your salon by area and enable geo search."}
                  </p>
                </div>

                <div className="border-t border-[var(--border-subtle)] pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-[10px] uppercase text-[var(--text-muted)] font-semibold tracking-wider">
                      {lang === "ar" ? "إحداثيات الفرع على الخريطة" : "Branch coordinates"}
                    </label>
                  </div>
                  <CoordsInput
                    lat={form.lat}
                    lng={form.lng}
                    onChange={({ lat, lng }) => setForm({ ...form, lat, lng })}
                    lang={lang}
                    testIdPrefix="branch-coords"
                  />
                  <p className="text-[11px] text-[var(--text-muted)] mt-2">
                    {lang === "ar"
                      ? "تظهر هذه الإحداثيات في الماركت بليس كنقطة على الخريطة وتستخدم لحساب المسافات من العميل."
                      : "These coordinates are used on the Marketplace map to show your salon and compute distances from customers."}
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-2 mt-5">
              <button onClick={() => setOpen(false)} className="flex-1 px-4 py-2 border rounded-md">{lang === "ar" ? "إلغاء" : "Cancel"}</button>
              <button data-testid="save-branch-btn" onClick={save} className="flex-1 px-4 py-2 bg-[var(--gold)] text-white rounded-md">{lang === "ar" ? "حفظ" : "Save"}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
